import {
  NavbarTypes,
  SidebarTypes,
} from "../../theme/layoutConstants";

export type NavbarThemeCssVars = {
  "--navbar-bg": string;
  "--input-bg": string;
  "--input-color": string;
  "--navbar-icon-bg": string;
};

export const NAVBAR_THEME_VARS: Record<string, NavbarThemeCssVars> = {
  "#ffffff": {
    "--navbar-bg": "#FFFFFF",
    "--input-bg": "#F4F4F5",
    "--input-color": "#0C2236",
    "--navbar-icon-bg": "#0C2236",
  },
  "#e2e7ec": {
    "--navbar-bg": "#E2E7EC",
    "--input-bg": "#F4F4F5",
    "--input-color": "#0C2236",
    "--navbar-icon-bg": "#0C2236",
  },
  "#c9d1fb": {
    "--navbar-bg": "#C9D1FB",
    "--input-bg": "rgba(255, 255, 255, 0.55)",
    "--input-color": "#0C2236",
    "--navbar-icon-bg": "#0C2236",
  },
  "#d6e4ff": {
    "--navbar-bg": "#D6E4FF",
    "--input-bg": "rgba(255, 255, 255, 0.65)",
    "--input-color": "#0C2236",
    "--navbar-icon-bg": "#0C2236",
  },
  "#c1c3cf": {
    "--navbar-bg": "#C1C3CF",
    "--input-bg": "rgba(255, 255, 255, 0.5)",
    "--input-color": "#0C2236",
    "--navbar-icon-bg": "#0C2236",
  },
  "#f3d9e2": {
    "--navbar-bg": "#F3D9E2",
    "--input-bg": "rgba(255, 255, 255, 0.65)",
    "--input-color": "#0C2236",
    "--navbar-icon-bg": "#0C2236",
  },
  "#d8f3e8": {
    "--navbar-bg": "#D8F3E8",
    "--input-bg": "rgba(255, 255, 255, 0.65)",
    "--input-color": "#0C2236",
    "--navbar-icon-bg": "#0C2236",
  },
  "#ffe8d6": {
    "--navbar-bg": "#FFE8D6",
    "--input-bg": "rgba(255, 255, 255, 0.65)",
    "--input-color": "#0C2236",
    "--navbar-icon-bg": "#0C2236",
  },
  "#e8f1f8": {
    "--navbar-bg": "#E8F1F8",
    "--input-bg": "rgba(255, 255, 255, 0.7)",
    "--input-color": "#0C2236",
    "--navbar-icon-bg": "#0C2236",
  },
  "#e4d4f5": {
    "--navbar-bg": "#E4D4F5",
    "--input-bg": "rgba(255, 255, 255, 0.65)",
    "--input-color": "#0C2236",
    "--navbar-icon-bg": "#0C2236",
  },
  "#0c2236": {
    "--navbar-bg": "#0C2236",
    "--input-bg": "rgba(255, 255, 255, 0.3)",
    "--input-color": "#FFFFFF",
    "--navbar-icon-bg": "#C1C3CF",
  },
  "#6fb0f9": {
    "--navbar-bg": "rgba(111, 176, 249, 0.45)",
    "--input-bg": "rgba(255, 255, 255, 0.35)",
    "--input-color": "#0C2236",
    "--navbar-icon-bg": "#0C2236",
  },
};

function isLightHex(hex: string): boolean {
  const value = normalizeHex(hex).replace("#", "");
  if (value.length !== 6) return true;
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62;
}

export function normalizeHex(color: string | null | undefined): string {
  if (!color) return "#ffffff";
  let value = String(color).trim().toLowerCase();
  if (!value.startsWith("#")) {
    value = `#${value}`;
  }
  if (value === "#fff") return "#ffffff";
  if (value === "#000") return "#000000";
  return value;
}

export function hexToClassSuffix(color: string | null | undefined): string {
  return normalizeHex(color).replace("#", "");
}

export function sameColor(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  return normalizeHex(a) === normalizeHex(b);
}

export function getNavbarThemeVars(
  color: string | null | undefined,
): NavbarThemeCssVars {
  const hex = normalizeHex(color);
  if (NAVBAR_THEME_VARS[hex]) {
    return NAVBAR_THEME_VARS[hex];
  }
  const light = isLightHex(hex);
  return {
    "--navbar-bg": hex,
    "--input-bg": light ? "rgba(255, 255, 255, 0.65)" : "rgba(255, 255, 255, 0.28)",
    "--input-color": light ? "#0C2236" : "#FFFFFF",
    "--navbar-icon-bg": light ? "#0C2236" : "#C1C3CF",
  };
}

export const THEME_DEFAULTS = {
  dashboardTheme: "wave",
  navbarColor: "#FFFFFF",
  navbarType: NavbarTypes.STATIC,
  sidebarType: SidebarTypes.SOLID,
} as const;

export function getDashboardThemeClass(
  dashboardTheme: string | null | undefined,
  sidebarType: string | null | undefined,
): string {
  if (sidebarType === SidebarTypes.TRANSPARENT) {
    return "dashboard-light";
  }
  return `dashboard-${dashboardTheme || THEME_DEFAULTS.dashboardTheme}`;
}

export function getHeaderThemeClass(
  navbarColor: string | null | undefined,
): string {
  return `header-${hexToClassSuffix(navbarColor)}`;
}

export function persistThemeSetting(key: string, value: string): void {
  localStorage.setItem(key, value);
}
